import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  LinearProgress,
  Alert,
  Button,
  Grid,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import {
  GetApp as DownloadIcon,
  CheckCircle as ApproveIcon,
  Delete as DeleteIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import Meta from "../../components/common/Meta";
import { DrawerHeader } from "../../components/layout/Drawer/DrawerMain";
import { uiHeight } from "../../utils/uiHeight";
import { ResearchTabGroup } from "./components/ResearchTabGroup";
import { FinalReportCard } from "./components/FinalReportCard";
import { GradientTypography } from "../../utils/gradientTypography";
import { ResearchProvider } from "../../context/ResearchContext";
import { useResearchActions } from "../../hooks/useResearchActions";
import { Images } from "../../assets";
import { useCustomNavigate } from "../../hooks/useCustomNavigate";

const ResearchProgressContent: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useCustomNavigate();
  const { t } = useTranslation();

  // 削除確認ダイアログの状態
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const {
    // State
    research,
    editingPlan,
    approving,
    loading,
    error,

    // Actions
    loadResearch,
    editResearchPlan,
    approvePlanAndContinue,
    deleteResearch,

    // Download menu state and handlers
    downloadMenuAnchor,
    downloadMenuOpen,
    handleDownloadMenuOpen,
    handleDownloadMenuClose,
    handleDownloadFormat,
  } = useResearchActions(uuid);

  // 最終レポートがあるかどうかを判定
  const hasReport = research && research.final_report && research.final_report.trim().length > 0;

  useEffect(() => {
    if (uuid) {
      loadResearch();
    }
  }, [uuid, loadResearch]);

  const handleEditQuery = async (editQuery: string) => {
    await editResearchPlan(editQuery);
  };

  const handleApprovePlan = async () => {
    await approvePlanAndContinue();
  };

  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    if (!deleting) {
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      const success = await deleteResearch();
      if (success) {
        // 削除が成功した場合は調査一覧に戻る
        navigate("/deep-research");
      }
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleSaveToTextFile = () => {
    if (research && research.final_report) {
      const title = research.title || research.query;
      navigate("/notes-nebula/files/new", {
        state: {
          content: research.final_report,
          title: title.length > 36 ? title.slice(0, 36) : title,
        },
      });
    }
  };

  if (loading) {
    return (
      <Box>
        <Meta title="Deep Research" meta={[{ name: "robots", content: "noindex, nofollow" }]} />
        <DrawerHeader />
        <Container
          maxWidth="xl"
          sx={{
            pb: 2,
            display: "flex",
            flexDirection: "column",
            height: uiHeight(),
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <LinearProgress sx={{ mb: 2, width: "100%", maxWidth: 400 }} />
          <Typography>{t("deepResearch.progress.loading")}</Typography>
        </Container>
      </Box>
    );
  }

  if (error || !research) {
    return (
      <Box>
        <Meta title="Deep Research" meta={[{ name: "robots", content: "noindex, nofollow" }]} />
        <DrawerHeader />
        <Container
          maxWidth="xl"
          sx={{
            pb: 2,
            display: "flex",
            flexDirection: "column",
            height: uiHeight(),
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || t("deepResearch.progress.notFound")}
          </Alert>
          <Button disableElevation variant="contained" onClick={() => navigate("/deep-research")}>
            {t("deepResearch.progress.backToList")}
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <Meta title={research.title || research.query} meta={[{ name: "robots", content: "noindex, nofollow" }]} />
      <DrawerHeader />

      <Container
        maxWidth="xl"
        sx={{
          pb: 2,
          display: "flex",
          flexDirection: "column",
          height: uiHeight(),
        }}
      >
        {/* ヘッダー情報 */}
        <Box sx={{ flexShrink: 0 }}>
          {/* タイトル行とアクションボタン */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box
              sx={{ display: "flex", flexDirection: "row", fontWeight: "bold", zIndex: 100, alignItems: "flex-end" }}
            >
              <GradientTypography
                variant="h4"
                sx={{ fontWeight: 600, cursor: "pointer", mb: 0.5 }}
                component="h1"
                onClick={() => navigate("/deep-research")}
              >
                {t("deepResearch.title")}
              </GradientTypography>
              <Typography variant="subtitle1" component="span" sx={{ ml: 1, mb: 0.5 }} color={"#fff"} gutterBottom>
                Beta
              </Typography>
            </Box>

            {/* アクションボタン */}
            <Box sx={{ display: "flex", gap: 1 }}>
              {research.status === "completed" && (
                <>
                  <Button
                    disableElevation
                    variant="outlined"
                    startIcon={<img src={Images.notesNebula.ico} alt="save" style={{ height: 20 }} />}
                    onClick={handleSaveToTextFile}
                  >
                    {t("deepResearch.progress.saveToTextFile")}
                  </Button>
                  <Button
                    disableElevation
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    endIcon={<ArrowDownIcon />}
                    onClick={handleDownloadMenuOpen}
                  >
                    {t("deepResearch.progress.downloadReport")}
                  </Button>
                  <Menu
                    anchorEl={downloadMenuAnchor}
                    open={downloadMenuOpen}
                    onClose={handleDownloadMenuClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    <MenuItem onClick={() => handleDownloadFormat("markdown")}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Markdown (.md)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t("deepResearch.downloadFormats.markdownDescription")}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem onClick={() => handleDownloadFormat("word")}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Word (.docx)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t("deepResearch.downloadFormats.wordDescription")}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem onClick={() => handleDownloadFormat("txt")}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Text (.txt)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t("deepResearch.downloadFormats.txtDescription")}
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Menu>
                </>
              )}
              {research.status === "plan_pending" && research.plan_text && !editingPlan && (
                <Button
                  disableElevation
                  variant="contained"
                  color="primary"
                  startIcon={<ApproveIcon />}
                  onClick={handleApprovePlan}
                  disabled={approving}
                >
                  {approving ? t("deepResearch.progress.approving") : t("deepResearch.progress.approvePlan")}
                </Button>
              )}
              {["completed", "error", "plan_pending"].includes(research.status) && (
                <Button
                  disableElevation
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteDialogOpen}
                >
                  {t("deepResearch.progress.deleteResearch")}
                </Button>
              )}
              <Button disableElevation variant="outlined" onClick={() => navigate("/deep-research")}>
                {t("deepResearch.progress.backToList")}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* メインコンテンツ */}
        <Grid container spacing={2} sx={{ flex: 1, overflow: "hidden", height: "100%" }}>
          {/* 左側: 調査計画・進捗・情報源 */}
          <Grid
            size={{
              xs: 12,
              lg: hasReport ? 6 : 12,
            }}
            sx={{ height: "100%" }}
          >
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                backgroundColor: (theme) => alpha(theme.palette.background.custom1, 0.5),
                backdropFilter: "blur(10px)",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {/* エラーメッセージ */}
              {research.error_message && (
                <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
                  {research.error_message}
                </Alert>
              )}

              {/* 調査情報・情報源・計画のタブ（コンテキストを使用、propsを削減） */}
              <ResearchTabGroup onEditQuery={handleEditQuery} />
            </Box>
          </Grid>

          {/* 右側: 最終レポート（レポートがある場合のみ） */}
          {hasReport && (
            <Grid
              size={{
                xs: 12,
                lg: 6,
              }}
              sx={{ height: "100%", display: "flex" }}
            >
              <Box
                sx={{
                  display: "flex",
                  height: "100%",
                  width: "100%",
                  backgroundColor: (theme) => alpha(theme.palette.background.custom1, 0.5),
                  backdropFilter: "blur(10px)",
                  borderRadius: 2,
                }}
              >
                <FinalReportCard
                  finalReport={research.final_report}
                  status={research.status}
                  researchMode={research.research_mode}
                />
              </Box>
            </Grid>
          )}

          {/* レポートがない場合は下部に表示 */}
          {!hasReport && (
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  backgroundColor: (theme) => alpha(theme.palette.background.custom1, 0.5),
                  backdropFilter: "blur(10px)",
                  borderRadius: 2,
                }}
              >
                <FinalReportCard
                  finalReport={research.final_report}
                  status={research.status}
                  researchMode={research.research_mode}
                />
              </Box>
            </Grid>
          )}
        </Grid>

        {/* 削除確認ダイアログ */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} maxWidth="xs" fullWidth>
          <DialogTitle>{t("deepResearch.confirmations.deleteTitle")}</DialogTitle>
          <DialogContent>
            <Typography>{t("deepResearch.confirmations.deleteResearch")}</Typography>
          </DialogContent>
          <DialogActions>
            <Button disableElevation onClick={handleDeleteDialogClose} disabled={deleting} variant="outlined">
              {t("deepResearch.actions.cancel")}
            </Button>
            <Button
              disableElevation
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            >
              {deleting ? t("deepResearch.confirmations.deleting") : t("deepResearch.confirmations.delete")}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

const ResearchProgress: React.FC = () => {
  return (
    <ResearchProvider>
      <ResearchProgressContent />
    </ResearchProvider>
  );
};

export default ResearchProgress;
